import Style from "@client/controls/HTML/Style/component";
import { HtmlVideo, YouTubeVideo } from "@client/controls/Video/component";
import { getAssetUrl } from "@client/lib/assets";
import { roundTo } from "@client/lib/math";
import { isDefined } from "@client/lib/misc";
import { getImage } from "@client/lib/settings";
import { BackgroundImageSize, BackgroundType, type CompositeSettings, type GenericMap, MediaType } from "@client/types";
import * as React from "react";
import colorCtpl from "./color.ctpl";
import imageCtpl from "./image.ctpl";
import videoCtpl from "./video.ctpl";

import "./style.less";

type ContentProps = {
  containerSelector: string;
  radius: string | undefined;
  value: CompositeSettings.BackgroundHover;
};
export const Content = ({ containerSelector, value, ...props }: ContentProps) => {
  const colorData = React.useMemo(() => {
    if (value.type !== BackgroundType.Color) return null;

    return {
      containerSelector,
      background: `color-mix(in srgb, ${value.color ?? "var(--color)"} ${value.opacity || "100%"}, transparent)`,
      color: value.color ?? "var(--color)",
      hoverColor: value.hoverColor,
      radius: props.radius || "0px"
    };
  }, [containerSelector, props.radius, value]);

  const imageUrl = React.useMemo(
    // TODO: pull resized image?
    () => (value.image ? getAssetUrl(getImage(value.image), MediaType.Image) : undefined),
    [value.image]
  );

  const videoUrl = React.useMemo(
    // TODO: pull resized image?
    () => (value.video ? getAssetUrl(value.video, MediaType.Video) : undefined),
    [value.video]
  );

  const imageData = React.useMemo(() => {
    if (value.type === BackgroundType.Image && value.image) {
      const result: GenericMap<any> = {
        // ...getBorder(props.borderStyle),
        containerSelector,
        attachment: value.imageFixed ? "fixed" : "unset",
        backgroundImage: `url(${imageUrl})`,
        position: value.imagePosition || "unset",
        repeat: value.imageRepeat || "unset",
        radius: props.radius || "0px"
      };

      if (value.imageSize === BackgroundImageSize.Custom) {
        result.size = `${value.imageWidth} ${value.imageHeight}`;
      } else {
        result.size = value.imageSize || undefined;
      }

      if (value.overlay && value.overlay.opacity !== "100%") {
        const color = `color-mix(in srgb, ${value.overlay.color} ${value.overlay.opacity}, transparent)`;
        const background = `linear-gradient(${color}, ${color}), ${result.backgroundImage}`;

        result.altBackgroundImage = background;
      }

      return result;
    }
  }, [containerSelector, imageUrl, value, props.radius]);

  const videoData = React.useMemo(() => {
    if (value.type === BackgroundType.Video) {
      const data = {
        containerSelector
      };
      if (value.overlay) {
        Object.assign(data, {
          color: value.overlay.color,
          hoverColor: value.overlay.hoverColor,
          opacity: isDefined(value.overlay.opacity) ? roundTo(parseFloat(value.overlay.opacity) / 100, 2) : 1,
          radius: props.radius
        });
      }

      return data;
    }
  }, [containerSelector, value.type, value.overlay, props.radius]);

  return (
    <>
      {colorData && <Style ctpl={colorCtpl} data={colorData} />}
      {imageData && <Style ctpl={imageCtpl} data={imageData} />}
      {videoData && <Style ctpl={videoCtpl} data={videoData} />}

      {value.type === BackgroundType.Video && !!videoUrl && <HtmlVideo isBackground url={videoUrl} />}
      {value.type === BackgroundType.Video && !videoUrl && !!value.videoYouTubeID && (
        <YouTubeVideo isBackground videoId={value.videoYouTubeID} />
      )}
    </>
  );
};

export type BackgroundProps = {
  containerSelector: string;
  radius?: string;
  value: CompositeSettings.Background | null | undefined;
};

export const Background = (props: BackgroundProps) => {
  if (!props.value) return null;

  return <Content value={props.value} radius={props.radius} containerSelector={props.containerSelector} />;
};
